import type { MadinaPaymentStatusResponse } from 'TYPES/madina';

const SUCCESS_STATUSES = new Set(['PAID', 'SUCCESS', 'COMPLETED']);
const FAILURE_STATUSES = new Set(['FAILED', 'CANCELLED', 'CANCELED', 'DECLINED']);
const PAID_ORDER_STATUSES = new Set([
  'CONFIRMED',
  'PROCESSING',
  'DISPATCHED',
  'OUT_FOR_DELIVERY',
  'DELIVERED',
  'COMPLETED'
]);

/** Includes order.status — use for general UI only, not OnionPay callback success. */
export const extractPaymentStatus = (response: MadinaPaymentStatusResponse): string => {
  return String(
    response.payment_status ??
      response.payment?.payment_status ??
      response.order?.payment_status ??
      response.order?.status ??
      ''
  ).toUpperCase();
};

/** ERP callback: only payment fields from GET payment-status (never browser redirect). */
export const extractMadinaOrderPaymentStatus = (response: MadinaPaymentStatusResponse): string => {
  return String(
    response.payment_status ??
      response.payment?.payment_status ??
      response.order?.payment_status ??
      ''
  ).toUpperCase();
};

export const isMadinaOrderPaidFromPoll = (response: MadinaPaymentStatusResponse): boolean => {
  return isSuccessfulPaymentStatus(extractMadinaOrderPaymentStatus(response));
};

export const isSuccessfulPaymentStatus = (status: string): boolean => {
  return SUCCESS_STATUSES.has(status.toUpperCase());
};

export const isFailedPaymentStatus = (status: string): boolean => {
  return FAILURE_STATUSES.has(status.toUpperCase());
};

export const isPendingPaymentStatus = (status: string): boolean => {
  if (!status) return true;
  return !isSuccessfulPaymentStatus(status) && !isFailedPaymentStatus(status);
};

/** True when ERP reports payment complete (status poll or after verify). */
export const isMadinaPaymentComplete = (response: MadinaPaymentStatusResponse): boolean => {
  const paymentStatus = extractPaymentStatus(response);
  if (isSuccessfulPaymentStatus(paymentStatus)) {
    return true;
  }
  if (isFailedPaymentStatus(paymentStatus)) {
    return false;
  }

  const order = response.order;
  if (order?.inventory_deducted === true) {
    return true;
  }

  const orderStatus = String(order?.status ?? '').toUpperCase();
  if (PAID_ORDER_STATUSES.has(orderStatus) && paymentStatus !== 'PENDING') {
    return true;
  }

  return false;
};
